import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
} from "@mui/material";
import { Box } from "@mui/system";
import { DataGridPro, GridColDef } from "@mui/x-data-grid-pro";
import { useSelector } from "react-redux";
import { useMemo, useState } from "react";

interface SaveChangesAIProps {
  open: boolean;
  rowData: any;
  onClose: () => void;
  onConfirm?: (removedIds: string[], ignoredIds: string[]) => Promise<void>;
}

const SaveChangesAI = ({ open, onClose, onConfirm }: SaveChangesAIProps) => {
  const { allDefinitaions } = useSelector(
    (state: any) => state.aiRecomandationTab
  );
  const [isLoading, setIsLoading] = useState(false);

  // Process allDefinitaions to separate by definition type and collect shared_uids
  const { removedRows, ignoredRows, removedSharedUids, ignoredSharedUids } =
    useMemo(() => {
      const removed: any[] = [];
      const ignored: any[] = [];
      const adgroupsSet = new Set();
      const removedIds: string[] = [];
      const ignoredIds: string[] = [];

      Object.values(allDefinitaions).forEach((item: any, index) => {
        // Collect unique adgroups
        if (item.adgroup_name) {
          adgroupsSet.add(item.adgroup_name);
        }

        const rowData = {
          id: index + 1,
          keywordName: item.keyword_name || "",
          negativeSearchTerm: item.negative_term || "",
          matchType: item.match_type || "",
          positiveSearchTerm: item.similar_positive_term || "",
          estimatedMatchRelevance: item.similarity_score
            ? `${item.similarity_score}%`
            : "",
        };

        // Collect shared_uid for API calls
        if (item.shared_uid) {
          if (item.definition === "mark-as-removed") {
            removed.push(rowData);
            removedIds.push(item.shared_uid);
          } else if (item.definition === "mark-as-ignored") {
            ignored.push(rowData);
            ignoredIds.push(item.shared_uid);
          }
        }
      });

      return {
        removedRows: removed,
        ignoredRows: ignored,
        totalAdgroups: adgroupsSet.size,
        removedSharedUids: removedIds,
        ignoredSharedUids: ignoredIds,
      };
    }, [allDefinitaions]);

  // --- Define columns ---
  const columns: GridColDef[] = [
    {
      field: "keywordName",
      headerName: "Keyword Name",
      flex: 1,
      minWidth: 180,
    },
    {
      field: "negativeSearchTerm",
      headerName: "Negative Search Term",
      flex: 1,
      minWidth: 180,
    },
    {
      field: "matchType",
      headerName: "Match Type",
      flex: 0.6,
      minWidth: 110,
    },
    {
      field: "positiveSearchTerm",
      headerName: "Positive Search Term",
      flex: 1,
      minWidth: 180,
    },
    {
      field: "estimatedMatchRelevance",
      headerName: "Estimated Match Relevance",
      flex: 0.8,
      minWidth: 180,
    },
  ];

  const handleConfirm = async () => {
    setIsLoading(true);

    try {
      // Call the parent's onConfirm handler with the grouped shared_uids
      if (onConfirm) {
        await onConfirm(removedSharedUids, ignoredSharedUids);
      }

      // Close the dialog after successful API calls
      onClose();
    } catch (error) {
      console.error("Error confirming changes:", error);
      // You might want to show an error message to the user here
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Total Number of Adgroups Affected:{" "}
        {removedRows.length + ignoredRows.length}
      </DialogTitle>
      <DialogContent>
        {/* Removed Definitions Section */}
        {removedRows.length > 0 && (
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              Negative Definition Removed ({removedRows.length})
            </Typography>
            <Box sx={{ width: "100%", display: "grid", mt: 1, mb: 3 }}>
              <DataGridPro
                sx={{
                  "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within":
                    {
                      outline: "none",
                    },
                  "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within":
                    { outline: "none" },
                  "& .MuiDataGrid-cellEmpty": {
                    width: "auto",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                  "& .MuiDataGrid-row.Mui-selected": {
                    backgroundColor: "rgba(156, 39, 176, 0.08) !important",
                    "&:hover": {
                      backgroundColor: "rgba(156, 39, 176, 0.16) !important",
                    },
                  },
                  "& .MuiDataGrid-row.Mui-selected .MuiDataGrid-cell--pinnedLeft":
                    {
                      backgroundColor: "#f0def3 !important",
                    },
                  "& .MuiDataGrid-row.Mui-selected:hover .MuiDataGrid-cell--pinnedLeft":
                    {
                      backgroundColor: "#e6d6e9ff !important",
                    },
                  "& .MuiDataGrid-row.Mui-selected .MuiDataGrid-cell--pinnedRight":
                    {
                      backgroundColor: "#f0def3 !important",
                    },
                  "& .MuiDataGrid-row.Mui-selected:hover .MuiDataGrid-cell--pinnedRight":
                    {
                      backgroundColor: "#e6d6e9ff !important",
                    },
                  "& .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-cell--pinnedRight":
                    {
                      backgroundColor: "#ffffff",
                    },
                  "& .MuiDataGrid-cell.Mui-selected": {
                    backgroundColor: "inherit !important",
                  },
                  "& .MuiCheckbox-root": {
                    color: "#9c27b0 !important",
                  },
                  "& .MuiCheckbox-root.Mui-checked": {
                    color: "#9c27b0",
                  },
                  border: "none",
                }}
                density="compact"
                hideFooter
                pagination={false}
                rows={removedRows}
                columns={columns}
                disableColumnMenu
                disableColumnFilter
                disableColumnReorder
                sortingOrder={["asc", "desc"]}
                isCellEditable={() => false}
                disableRowSelectionOnClick
              />
            </Box>
          </Box>
        )}

        {/* Ignored Definitions Section */}
        {ignoredRows.length > 0 && (
          <Box sx={{ mt: removedRows.length > 0 ? 2 : 0 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Ignored ({ignoredRows.length})
            </Typography>
            <Box sx={{ width: "100%", display: "grid", mt: 1 }}>
              <DataGridPro
                sx={{
                  "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within":
                    {
                      outline: "none",
                    },
                  "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within":
                    { outline: "none" },
                  "& .MuiDataGrid-cellEmpty": {
                    width: "auto",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                  "& .MuiDataGrid-row.Mui-selected": {
                    backgroundColor: "rgba(156, 39, 176, 0.08) !important",
                    "&:hover": {
                      backgroundColor: "rgba(156, 39, 176, 0.16) !important",
                    },
                  },
                  "& .MuiDataGrid-row.Mui-selected .MuiDataGrid-cell--pinnedLeft":
                    {
                      backgroundColor: "#f0def3 !important",
                    },
                  "& .MuiDataGrid-row.Mui-selected:hover .MuiDataGrid-cell--pinnedLeft":
                    {
                      backgroundColor: "#e6d6e9ff !important",
                    },
                  "& .MuiDataGrid-row.Mui-selected .MuiDataGrid-cell--pinnedRight":
                    {
                      backgroundColor: "#f0def3 !important",
                    },
                  "& .MuiDataGrid-row.Mui-selected:hover .MuiDataGrid-cell--pinnedRight":
                    {
                      backgroundColor: "#e6d6e9ff !important",
                    },
                  "& .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-cell--pinnedRight":
                    {
                      backgroundColor: "#ffffff",
                    },
                  "& .MuiDataGrid-cell.Mui-selected": {
                    backgroundColor: "inherit !important",
                  },
                  "& .MuiCheckbox-root": {
                    color: "#9c27b0 !important",
                  },
                  "& .MuiCheckbox-root.Mui-checked": {
                    color: "#9c27b0",
                  },
                  border: "none",
                }}
                density="compact"
                hideFooter
                pagination={false}
                rows={ignoredRows}
                columns={columns}
                disableColumnMenu
                disableColumnFilter
                disableColumnReorder
                sortingOrder={["asc", "desc"]}
                isCellEditable={() => false}
                disableRowSelectionOnClick
              />
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ padding: "16px 20px 16px" }}>
        <Button
          onClick={onClose}
          color="secondary"
          variant="outlined"
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          color="secondary"
          disabled={isLoading}
          startIcon={
            isLoading ? <CircularProgress size={20} color="secondary" /> : null
          }
          data-tracker="612"
        >
          {isLoading ? "Processing..." : "Confirm"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SaveChangesAI;
